import { useRef, useState } from 'react';
import { useProductsStore } from '@/hooks/useProductsStore';
import type { Product } from '@/mocks/admin';
import StatusBadge from '@/components/base/StatusBadge';
import { PRODUCT_CATEGORIES } from '@/constants/productCategories';

const formatPrice = (n: number) => `${n.toLocaleString('fr-FR')} FCFA`;
const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const ADMIN_PROFIT_FEE = 500;
const DEXPAY_PAYIN_RATE = 0.02;
const DEXPAY_PAYOUT_RATE = 0.02;
const PRODUCT_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=300&height=300&seq=admin-product-fallback-safe&orientation=squarish';
const PRODUCT_BADGES = ['Bestseller', 'Nouveau', 'Promo', 'Coup de coeur'];
const DAKAR_COMMUNES = [
  'Plateau', 'Médina', 'Grand Dakar', 'Fann – Point E – Amitié', 'Liberté',
  'Dieuppeul – Derklé', 'HLM', 'Biscuiterie', 'Gueule Tapée – Fass – Colobane',
  'Parcelles Assainies', 'Yoff', 'Almadies', 'Ngor', 'Ouakam', 'Mermoz – Sacré-Coeur',
  'Gorée', 'Pikine', 'Guédiawaye', 'Rufisque', 'Thiaroye', 'Mbao', 'Bargny',
  'Diamniadio', 'Sébikhotane', 'Sangalkam',
];
type Step = 1 | 2 | 3;

const handleProductImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = PRODUCT_FALLBACK_IMAGE;
};

const getApprovalPriceBreakdown = (price: number) => {
  if (price <= 0) {
    return { base: 0, admin: 0, payin: 0, payout: 0, total: 0 };
  }

  const subtotal = price + ADMIN_PROFIT_FEE;
  const payin = Math.ceil(subtotal * DEXPAY_PAYIN_RATE);
  const payout = Math.ceil(price * DEXPAY_PAYOUT_RATE);

  return {
    base: price,
    admin: ADMIN_PROFIT_FEE,
    payin,
    payout,
    total: Math.ceil(subtotal + payin + payout),
  };
};

const getProductPriceBreakdown = (product: Product) => {
  if (product.originalPrice !== undefined) {
    return {
      base: product.originalPrice,
      admin: product.adminProfitFee ?? 0,
      payin: product.dexpayPayinFee ?? 0,
      payout: product.dexpayPayoutFee ?? 0,
      total: product.price,
    };
  }

  return getApprovalPriceBreakdown(product.price);
};

export default function PendingProducts() {
  const { products, addProduct, approveProduct, rejectProduct, updateProduct, deleteProduct, restoreProduct } = useProductsStore();
  const [rejectTarget, setRejectTarget] = useState<Product | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'deleted'>('pending');
  const [detailTarget, setDetailTarget] = useState<Product | null>(null);
  const [editTarget, setEditTarget] = useState<Product | null>(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('Tous');
  const [articleFilter, setArticleFilter] = useState('Tous');
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<Step>(1);
  const [createForm, setCreateForm] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    condition: 'Neuf',
    badge: '',
    deliveryDelay: '',
    location: '',
    deliveryFee: '',
    weight: '',
    image: '',
    description: '',
  });
  const [createErrors, setCreateErrors] = useState<Partial<Record<string, string>>>({});
  const [createSaving, setCreateSaving] = useState(false);
  const [createImagePreviews, setCreateImagePreviews] = useState<string[]>([]);
  const [createImageFiles, setCreateImageFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    image: '',
    description: '',
  });

  const vendorOptions = ['Tous', ...Array.from(new Set(products.map((product) => product.vendorName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'fr'))];
  const articleOptions = ['Tous', ...Array.from(new Set(products.map((product) => product.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'fr'))];
  const filtered = products.filter((p) => {
    const query = searchTerm.trim().toLowerCase();
    const matchesStatus = filter === 'all' || p.status === filter;
    const matchesVendor = vendorFilter === 'Tous' || p.vendorName === vendorFilter;
    const matchesArticle = articleFilter === 'Tous' || p.category === articleFilter;
    const matchesSearch =
      !query ||
      p.name.toLowerCase().includes(query) ||
      p.category.toLowerCase().includes(query) ||
      p.vendorName.toLowerCase().includes(query);

    return matchesStatus && matchesVendor && matchesArticle && matchesSearch;
  });

  const approve = (id: string) => {
    approveProduct(id);
  };

  const reject = () => {
    if (!rejectTarget) return;
    rejectProduct(rejectTarget.id, rejectReason);
    setRejectTarget(null);
    setRejectReason('');
  };

  const openEdit = (product: Product) => {
    setEditTarget(product);
    setEditImageFile(null);
    setEditImagePreview(product.images[0] || '');
    setEditForm({
      name: product.name,
      category: product.category,
      price: String(product.price),
      stock: String(product.stock),
      image: product.images[0] || '',
      description: product.description,
    });
  };

  const handleEditImageFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEditImageFile(file);
    setEditImagePreview(preview);
    setEditForm((current) => ({ ...current, image: preview }));
  };

  const saveEdit = async () => {
    if (!editTarget) return;

    setEditSaving(true);
    setEditError('');

    try {
      await updateProduct(editTarget.id, {
        ...editTarget,
        name: editForm.name,
        category: editForm.category,
        price: Number(editForm.price || 0),
        stock: Number(editForm.stock || 0),
        images: [editImagePreview || editForm.image],
        imageFile: editImageFile,
        description: editForm.description,
      });

      setEditTarget(null);
      setEditImageFile(null);
      setEditImagePreview('');
    } catch (error) {
      setEditError(error instanceof Error ? error.message : 'Impossible de modifier le produit.');
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteProduct(deleteTarget.id);
    setDeleteTarget(null);
    if (detailTarget?.id === deleteTarget.id) {
      setDetailTarget(null);
    }
  };

  const restore = async (product: Product) => {
    await restoreProduct(product.id);
  };

  const saveCreate = async () => {
    setCreateSaving(true);
    setCreateErrors({});

    try {
      await addProduct({
        name: createForm.name,
        category: createForm.category,
        price: Number(createForm.price || 0),
        stock: Number(createForm.stock || 0),
        description: createForm.description,
        images: createImagePreviews.length > 0 ? createImagePreviews : [createForm.image || PRODUCT_FALLBACK_IMAGE],
        condition: createForm.condition as 'Neuf' | 'Occasion',
        badge: createForm.badge || undefined,
        deliveryDelay: createForm.deliveryDelay || undefined,
        location: createForm.location || undefined,
        deliveryFee: 'Gratuit',
        status: 'pending',
        vendorId: '',
        vendorName: 'Administration',
        createdAt: new Date().toISOString(),
        imageFile: createImageFiles[0] ?? null,
      });

      setCreateOpen(false);
      setCreateStep(1);
      setCreateForm({
        name: '',
        category: '',
        price: '',
        stock: '',
        condition: 'Neuf',
        badge: '',
        deliveryDelay: '',
        location: '',
        deliveryFee: '',
        weight: '',
        image: '',
        description: '',
      });
      setCreateErrors({});
      setCreateImageFiles([]);
      setCreateImagePreviews([]);
    } catch (error) {
      setCreateErrors({
        submit: error instanceof Error ? error.message : 'Impossible de créer le produit.',
      });
    } finally {
      setCreateSaving(false);
    }
  };

  const setCreateField = (key: keyof typeof createForm, value: string) => {
    setCreateForm((current) => ({ ...current, [key]: value }));
  };

  const handleCreateImageFiles = (files: FileList | null) => {
    if (!files) return;
    const remaining = 5 - createImagePreviews.length;
    if (remaining <= 0) return;
    const selected = Array.from(files).slice(0, remaining);
    const newPreviews = selected.map((file) => URL.createObjectURL(file));
    setCreateImageFiles((prev) => [...prev, ...selected]);
    setCreateImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeCreateImage = (index: number) => {
    setCreateImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    setCreateImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateCreateStep1 = () => {
    const errors: Partial<Record<string, string>> = {};
    if (createForm.name.length < 3) errors.name = 'Minimum 3 caractères';
    if (!createForm.category) errors.category = 'Requis';
    if (!createForm.price || Number(createForm.price) <= 0) errors.price = 'Prix doit être > 0';
    if (createForm.stock === '' || Number(createForm.stock) < 0) errors.stock = 'Stock ≥ 0';
    if (!createForm.description.trim()) errors.description = 'Requis';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCreateStep2 = () => {
    const errors: Partial<Record<string, string>> = {};
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNext = () => {
    if (createStep === 1 && validateCreateStep1()) setCreateStep(2);
    else if (createStep === 2 && validateCreateStep2()) setCreateStep(3);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateStep(1);
  };

  const filterButtons: { key: typeof filter; label: string }[] = [
    { key: 'all', label: 'Tous' },
    { key: 'pending', label: 'En attente' },
    { key: 'approved', label: 'Approuvés' },
    { key: 'rejected', label: 'Rejetés' },
    { key: 'deleted', label: 'Corbeille' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gold-100">
            <i className="ri-store-line text-gold-dark text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Gestion des Produits</h3>
            <p className="text-xs text-gray-400">
              {products.filter((p) => p.status === 'pending').length} en attente · {products.filter((p) => p.status === 'deleted').length} dans la corbeille
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {filterButtons.map((btn) => (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filter === btn.key ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {btn.key === 'deleted' ? <i className="ri-delete-bin-line mr-1"></i> : null}
              {btn.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white text-sm rounded-xl hover:bg-navy-lighter cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>Ajouter
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Rechercher un article..."
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
        </div>
        <select
          value={vendorFilter}
          onChange={(event) => setVendorFilter(event.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
        >
          {vendorOptions.map((vendor) => (
            <option key={vendor} value={vendor}>{vendor === 'Tous' ? 'Toutes les boutiques / vendeurs' : vendor}</option>
          ))}
        </select>
        <select
          value={articleFilter}
          onChange={(event) => setArticleFilter(event.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
        >
          {articleOptions.map((article) => (
            <option key={article} value={article}>{article === 'Tous' ? 'Tous les articles' : article}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50">
              <th className="text-left text-xs font-semibold text-gray-400 px-5 py-3">Produit</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Vendeur</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Prix</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Stock</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Statut</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Date</th>
              <th className="text-left text-xs font-semibold text-gray-400 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((product) => (
              <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={product.images[0]} alt={product.name} onError={handleProductImageError} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 truncate max-w-[160px]">{product.name}</p>
                      <p className="text-xs text-gray-400">{product.category}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{product.vendorName}</td>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900">{formatPrice(product.price)}</p>
                  {product.status === 'pending' && (
                    <p className="text-[11px] font-medium text-emerald-600">
                      Approuvé: {formatPrice(getProductPriceBreakdown(product).total)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-semibold ${product.stock === 0 ? 'text-red-500' : 'text-gray-700'}`}>
                    {product.stock}
                  </span>
                </td>
                <td className="px-4 py-3"><StatusBadge status={product.status} /></td>
                <td className="px-4 py-3 text-xs text-gray-400">{formatDate(product.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setDetailTarget(product)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                      title="Voir détails"
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                    {product.status === 'deleted' ? (
                      <button
                        onClick={() => void restore(product)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
                        title="Restaurer"
                      >
                        <i className="ri-arrow-go-back-line text-sm"></i>
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => openEdit(product)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gold-100 text-gold-dark transition-colors cursor-pointer"
                          title="Modifier"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteTarget(product)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-500 transition-colors cursor-pointer"
                          title="Mettre dans la corbeille"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </>
                    )}
                    {product.status === 'pending' && (
                      <>
                        <button
                          onClick={() => approve(product.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-500 transition-colors cursor-pointer"
                          title="Approuver"
                        >
                          <i className="ri-check-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => { setRejectTarget(product); setRejectReason(''); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-500 transition-colors cursor-pointer"
                          title="Rejeter"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <i className="ri-inbox-line text-3xl block mb-2"></i>
            <p className="text-sm">Aucun produit dans cette catégorie</p>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRejectTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                <i className="ri-close-circle-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">Rejeter le produit</h4>
                <p className="text-xs text-gray-400 truncate max-w-[260px]">{rejectTarget.name}</p>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Raison du rejet</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Expliquez pourquoi ce produit est rejeté..."
              className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-red-400"
              maxLength={500}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{rejectReason.length}/500</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRejectTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={reject} disabled={!rejectReason.trim()} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold disabled:opacity-50 hover:bg-red-600 cursor-pointer whitespace-nowrap">Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDetailTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">Détails du produit</h4>
              <button onClick={() => setDetailTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <img src={detailTarget.images[0]} alt={detailTarget.name} onError={handleProductImageError} className="w-full h-40 object-cover rounded-xl mb-4 bg-gray-100" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Nom', value: detailTarget.name },
                { label: 'Catégorie', value: detailTarget.category },
                { label: 'Prix', value: formatPrice(detailTarget.price) },
                { label: 'Stock', value: `${detailTarget.stock} unités` },
                { label: 'État', value: detailTarget.condition },
                { label: 'Vendeur', value: detailTarget.vendorName },
                { label: 'Lieu', value: detailTarget.location },
                { label: 'Livraison', value: detailTarget.deliveryDelay },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 mb-4 border border-emerald-100">
              <p className="text-xs text-emerald-700 font-semibold mb-2">Prix avec bénéfice admin et frais Dexpay</p>
              {(() => {
                const breakdown = getProductPriceBreakdown(detailTarget);
                return (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-gray-500">Prix vendeur</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.base)}</span>
                    <span className="text-gray-500">Bénéfice admin</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.admin)}</span>
                    <span className="text-gray-500">Frais payin Dexpay</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.payin)}</span>
                    <span className="text-gray-500">Frais payout Dexpay</span><span className="font-semibold text-gray-900 text-right">{formatPrice(breakdown.payout)}</span>
                    <span className="font-bold text-emerald-700">Prix approuvé</span><span className="font-bold text-emerald-700 text-right">{formatPrice(breakdown.total)}</span>
                  </div>
                );
              })()}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-400 mb-1">Description</p>
              <p className="text-sm text-gray-700">{detailTarget.description}</p>
            </div>
            {detailTarget.rejectionReason && (
              <div className="bg-red-50 rounded-lg p-3 mb-4 border border-red-100">
                <p className="text-xs text-red-500 font-semibold mb-1">Raison du rejet</p>
                <p className="text-sm text-red-700">{detailTarget.rejectionReason}</p>
              </div>
            )}
            <StatusBadge status={detailTarget.status} size="md" />
          </div>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">Modifier le produit</h4>
              <button onClick={() => setEditTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={editForm.name} onChange={(e) => setEditForm((current) => ({ ...current, name: e.target.value }))} placeholder="Nom" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.category} onChange={(e) => setEditForm((current) => ({ ...current, category: e.target.value }))} placeholder="Catégorie" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.price} onChange={(e) => setEditForm((current) => ({ ...current, price: e.target.value }))} placeholder="Prix" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.stock} onChange={(e) => setEditForm((current) => ({ ...current, stock: e.target.value }))} placeholder="Stock" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
            </div>
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <img
                  src={editImagePreview || editForm.image || PRODUCT_FALLBACK_IMAGE}
                  alt={editForm.name || 'Produit'}
                  onError={handleProductImageError}
                  className="h-16 w-16 rounded-xl object-cover bg-gray-100"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-500">Image du produit</p>
                  <p className="truncate text-xs text-gray-400">{editImageFile ? editImageFile.name : 'Image actuelle ou URL'}</p>
                </div>
                <button type="button" onClick={() => editFileInputRef.current?.click()} className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white">
                  Changer
                </button>
              </div>
              <input ref={editFileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleEditImageFile(e.target.files)} />
              <input value={editImageFile ? '' : editForm.image} onChange={(e) => {
                setEditImageFile(null);
                setEditImagePreview(e.target.value);
                setEditForm((current) => ({ ...current, image: e.target.value }));
              }} placeholder="URL image" className="mt-3 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
            </div>
            <textarea value={editForm.description} onChange={(e) => setEditForm((current) => ({ ...current, description: e.target.value }))} placeholder="Description" className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-gold/40 mt-3" />
            {editError ? <p className="mt-3 text-sm font-medium text-red-600">{editError}</p> : null}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void saveEdit()} disabled={editSaving} className="flex-1 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-light cursor-pointer whitespace-nowrap disabled:opacity-60">{editSaving ? 'Enregistrement...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-lg">
                  <i className="ri-add-line text-navy text-sm"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Ajouter un produit</h3>
                  <p className="text-xs text-gray-400">Même formulaire que dans l’espace vendeur</p>
                </div>
              </div>
              <button onClick={closeCreateModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0 overflow-x-auto">
              {([
                { step: 1, title: 'Informations produit' },
                { step: 2, title: 'Livraison' },
                { step: 3, title: 'Aperçu' },
              ] as const).map((item) => (
                <div key={item.step} className="flex items-center gap-3 min-w-fit">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${createStep >= item.step ? 'bg-gold text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {item.step}
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${createStep >= item.step ? 'text-navy' : 'text-gray-400'}`}>{item.title}</p>
                  </div>
                  {item.step < 3 && <div className="w-10 h-px bg-gray-200"></div>}
                </div>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              <div className="max-w-2xl space-y-4">
                {createStep === 1 && (
                  <>
                    <h3 className="text-sm font-bold text-gray-900 mb-2">Informations du produit</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du produit *</label>
                        <input value={createForm.name} onChange={e=>setCreateField('name',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Robe Wax Premium" />
                        {createErrors.name && <p className="text-xs text-red-500 mt-1">{createErrors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie *</label>
                        <select value={createForm.category} onChange={e=>setCreateField('category',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                          <option value="">Choisir une catégorie</option>
                          {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {createErrors.category && <p className="text-xs text-red-500 mt-1">{createErrors.category}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">État *</label>
                        <div className="flex gap-2">
                          {['Neuf', 'Occasion'].map(c => (
                            <button key={c} type="button" onClick={() => setCreateField('condition', c)}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${createForm.condition === c ? 'border-gold bg-gold-50 text-gold-dark' : 'border-gray-200 text-gray-500'}`}>{c}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Prix (FCFA) *</label>
                        <input type="number" value={createForm.price} onChange={e=>setCreateField('price',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                        {createErrors.price && <p className="text-xs text-red-500 mt-1">{createErrors.price}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Stock disponible *</label>
                        <input type="number" value={createForm.stock} onChange={e=>setCreateField('stock',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                        {createErrors.stock && <p className="text-xs text-red-500 mt-1">{createErrors.stock}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Étiquette produit</label>
                        <select value={createForm.badge} onChange={e=>setCreateField('badge',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                          <option value="">Aucune étiquette</option>
                          {PRODUCT_BADGES.map((badge) => <option key={badge} value={badge}>{badge}</option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Description *</label>
                        <textarea value={createForm.description} onChange={e=>setCreateField('description',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24" placeholder="Décrivez votre produit..." maxLength={500} />
                        <div className="flex justify-between items-center mt-1">
                          {createErrors.description ? <p className="text-xs text-red-500">{createErrors.description}</p> : <span/>}
                          <p className="text-xs text-gray-400">{createForm.description.length}/500</p>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Images du produit (min 1, max 5)</label>
                        <div className="flex items-center gap-3 flex-wrap">
                          {createImagePreviews.map((img, i) => (
                            <div key={i} className="w-20 h-20 rounded-xl overflow-hidden border border-gray-200 relative group flex-shrink-0">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeCreateImage(i)}
                                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-white text-sm"></i>
                              </button>
                            </div>
                          ))}
                          {createImagePreviews.length < 5 && (
                            <>
                              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleCreateImageFiles(e.target.files)} />
                              <button type="button" onClick={() => fileInputRef.current?.click()} className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-all cursor-pointer flex-shrink-0">
                                <i className="ri-add-line text-xl"></i>
                                <span className="text-[10px] mt-0.5">Ajouter</span>
                              </button>
                            </>
                          )}
                        </div>
                        {createImagePreviews.length === 0 && (
                          <p className="text-xs text-gray-400 mt-1">Aucune image sélectionnée — cliquez sur &quot;Ajouter&quot; pour choisir des photos depuis votre appareil.</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {createStep === 2 && (
                  <>
                    <h3 className="text-sm font-bold text-gray-900 mb-2">Informations de livraison</h3>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Délai de livraison (optionnel)</label>
                      <input value={createForm.deliveryDelay} onChange={e=>setCreateField('deliveryDelay',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 2–3 jours" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Localisation du vendeur (optionnel)</label>
                      <select value={createForm.location} onChange={e=>setCreateField('location',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                        <option value="">Choisir une commune de Dakar</option>
                        {DAKAR_COMMUNES.map(c=><option key={c} value={`${c}, Dakar`}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Poids / Taille (optionnel)</label>
                      <input value={createForm.weight} onChange={e=>setCreateField('weight',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 500g / Taille M" />
                    </div>
                  </>
                )}

                {createStep === 3 && (
                  <>
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Aperçu de votre produit</h3>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                      <div className="flex items-start gap-4">
                        {createImagePreviews.length > 0 ? (
                          <img src={createImagePreviews[0]} alt="" onError={handleProductImageError} className="w-24 h-24 rounded-xl object-cover bg-gray-200 flex-shrink-0" />
                        ) : (
                          <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-image-line text-gray-300 text-3xl"></i>
                          </div>
                        )}
                        <div>
                          <p className="text-base font-bold text-gray-900">{createForm.name || 'Nom du produit'}</p>
                          <p className="text-sm text-gray-500">{createForm.category || 'Catégorie'}</p>
                          {createForm.badge ? <p className="inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-bold bg-gold-100 text-gold-dark">{createForm.badge}</p> : null}
                          <p className="text-lg font-bold text-emerald-600 mt-1">{createForm.price ? formatPrice(Number(createForm.price)) : '—'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { l: 'Stock', v: createForm.stock || '—' }, { l: 'État', v: createForm.condition },
                          { l: 'Livraison', v: createForm.deliveryDelay || '—' }, { l: 'Lieu', v: createForm.location || '—' },
                          { l: 'Poids/Taille', v: createForm.weight || '—' },
                        ].map(item => (
                          <div key={item.l} className="bg-white rounded-lg p-2.5">
                            <p className="text-xs text-gray-400">{item.l}</p>
                            <p className="text-sm font-semibold text-gray-900">{item.v}</p>
                          </div>
                        ))}
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{createForm.description || '—'}</p>
                      </div>
                      <div className="flex items-center gap-2 bg-amber-50 p-3 rounded-xl">
                        <i className="ri-information-line text-amber-600"></i>
                        <p className="text-xs text-amber-700">Après soumission, votre produit sera examiné par l'admin avant publication.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="px-6 pb-6 pt-4 flex items-center gap-3 border-t border-gray-100">
              {createErrors.submit ? <p className="text-sm font-medium text-red-600">{createErrors.submit}</p> : null}
              {createStep > 1 && <button onClick={() => setCreateStep((s) => (s - 1) as Step)} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">← Retour</button>}
              <div className="flex-1"></div>
              {createStep < 3
                ? <button onClick={handleCreateNext} className="flex items-center gap-1.5 px-6 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-lighter cursor-pointer whitespace-nowrap">Continuer →</button>
                : <button onClick={() => void saveCreate()} disabled={createSaving} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gold to-gold-dark text-white rounded-xl text-sm font-semibold hover:from-gold-dark hover:to-[#a07828] cursor-pointer whitespace-nowrap disabled:opacity-60"><i className={createSaving ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>{createSaving ? 'Envoi...' : 'Soumettre pour validation'}</button>
              }
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-sm font-bold text-gray-900 mb-2">Supprimer ce produit ?</h4>
            <p className="text-sm text-gray-500 mb-4">Le produit <strong>{deleteTarget.name}</strong> sera déplacé dans la corbeille et ne sera plus visible dans l’application.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void confirmDelete()} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
